import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetAssessmentRequestContactsByVqa } from "./queries";
import {
  contactNotesMapFromContacts,
  resolvePrimarySponsorContactContext,
  shouldShowIntroMeetingDatePicker,
} from "../utils/assessmentAdminFieldConfig";
import {
  buildAdminExtendedFieldsFromForm,
  buildAdminExtendedFieldsFromVqa,
  getIntroMeetingReadDisplayKind,
  hydrateAdminExtendedFormValues,
  resolveIntroMeetingNotRequiredEffective,
} from "../utils/assessmentAdminFormHelpers";

/**
 * Contacts, notes draft, hydration, and intro-meeting display state for
 * ManageAssessments admin extended fields.
 */
export function useAssessmentAdminExtendedForm({
  vqaDetails,
  setValue,
  watch,
  updateActive,
}) {
  const queryClient = useQueryClient();
  const {
    data: requestContacts = [],
    refetch: refetchRequestContacts,
  } = useGetAssessmentRequestContactsByVqa(vqaDetails?.vqa?.vqa_id);

  const [contactNotesByEmail, setContactNotesByEmail] = useState({});
  const [sponsorContactNotesByEmail, setSponsorContactNotesByEmail] =
    useState({});

  const sponsorContactContext = useMemo(
    () => resolvePrimarySponsorContactContext(vqaDetails),
    [vqaDetails]
  );
  const { sponsorId: sponsorContactsSponsorId, contacts: sponsorContacts } =
    sponsorContactContext;

  const sponsorContactsNotesKey = useMemo(
    () =>
      (sponsorContacts ?? [])
        .map((c) => `${String(c?.email ?? "").toLowerCase()}:${c?.notes ?? ""}`)
        .join("|"),
    [sponsorContacts]
  );

  useEffect(() => {
    if (vqaDetails?.vqa?.diligent_notes !== undefined) {
      const { diligentNotes } = hydrateAdminExtendedFormValues(vqaDetails.vqa);
      setValue("diligentNotes", diligentNotes, { shouldValidate: false });
    }
  }, [vqaDetails?.vqa?.diligent_notes, setValue]);

  useEffect(() => {
    if (vqaDetails?.vqa) {
      const { introMeetingNotRequired } = hydrateAdminExtendedFormValues(
        vqaDetails.vqa
      );
      setValue("introMeetingNotRequired", introMeetingNotRequired, {
        shouldValidate: false,
      });
    }
  }, [vqaDetails?.vqa?.intro_meeting_not_required, setValue]);

  useEffect(() => {
    setContactNotesByEmail(contactNotesMapFromContacts(requestContacts));
  }, [requestContacts]);

  useEffect(() => {
    setSponsorContactNotesByEmail(contactNotesMapFromContacts(sponsorContacts));
  }, [sponsorContactsNotesKey]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed by contact email+notes

  const introMeetingNotRequiredWatched = watch("introMeetingNotRequired");
  const introMeetingNotRequiredEffective =
    resolveIntroMeetingNotRequiredEffective(
      introMeetingNotRequiredWatched,
      vqaDetails?.vqa?.intro_meeting_not_required
    );
  const showIntroMeetingDatePicker = shouldShowIntroMeetingDatePicker(
    updateActive,
    introMeetingNotRequiredEffective
  );
  const introMeetingReadKind = getIntroMeetingReadDisplayKind(
    vqaDetails?.vqa
  );

  const buildExtendedFromVqa = () =>
    buildAdminExtendedFieldsFromVqa(vqaDetails?.vqa, {
      requestContacts,
      contactNotesByEmail,
      sponsorContacts,
      sponsorContactNotesByEmail,
      sponsorContactsSponsorId,
    });

  const buildExtendedFromForm = (data) =>
    buildAdminExtendedFieldsFromForm(data, {
      watchedIntroMeetingNotRequired: watch("introMeetingNotRequired"),
      fallbackIntroMeetingDate:
        vqaDetails?.vqa?.intro_meeting_complete_date || null,
      requestContacts,
      contactNotesByEmail,
      sponsorContacts,
      sponsorContactNotesByEmail,
      sponsorContactsSponsorId,
    });

  const afterSuccessfulUpdate = () => {
    refetchRequestContacts();
    queryClient.invalidateQueries({
      queryKey: ["assessmentRequestContactsByVqa"],
    });
  };

  const onContactNotesChange = (emailKey, value) => {
    setContactNotesByEmail((prev) => ({
      ...prev,
      [emailKey]: value,
    }));
  };

  const onSponsorContactNotesChange = (emailKey, value) => {
    setSponsorContactNotesByEmail((prev) => ({
      ...prev,
      [emailKey]: value,
    }));
  };

  return {
    requestContacts,
    contactNotesByEmail,
    onContactNotesChange,
    sponsorContactNotesByEmail,
    onSponsorContactNotesChange,
    showIntroMeetingDatePicker,
    introMeetingReadKind,
    buildExtendedFromVqa,
    buildExtendedFromForm,
    afterSuccessfulUpdate,
  };
}
